// Section 24.14: the store is the only stateful module in the UI layer. Four signals: the game,
// the decision draft, the season status (24.7 state machine) and UI state.
import { signal } from '@preact/signals';
import type { Decision, GameState, PersonId, PromptId, SimErrorCode } from '../sim/api';
import { DEFAULT_SETTINGS, type SaveMeta, type Settings } from '../persist';
import {
  initialPeopleView,
  type AutoFillProposal,
  type ListFilter,
  type Notice,
  type OlderBalance,
  type PeopleView,
} from './types';

/** Section 24.7 season status, derived from the game plus whether the report was dismissed. */
export type SeasonStatus = 'title' | 'reporting' | 'deciding' | 'resolving' | 'paused' | 'ended';

/** Section 22 main-screen tabs. Each tab root lives in `src/ui/<tab>/<tab>-tab.tsx`. */
export type Tab =
  | 'people'
  | 'jobs'
  | 'rations'
  | 'build'
  | 'policies'
  | 'society'
  | 'health'
  | 'history'
  /** Dev builds only (24.15). */
  | 'debug';

/** Section 22 overlays: report, shortage dialog, event prompt, ledger, settings, how to play. */
export type Overlay =
  | 'report'
  | 'shortage'
  | 'event'
  | 'ledger'
  | 'settings'
  | 'howToPlay'
  | 'person'
  /** Save, load, export and import (stream F). */
  | 'saves'
  | null;

export interface UiError {
  code: SimErrorCode | 'unknown';
  message: string;
  diagnostics: string;
}

export interface UiState {
  tab: Tab;
  /** The report overlay is open at season start (Section 22). */
  overlay: Overlay;
  selectedPersonId: PersonId | null;
  error: UiError | null;
  /** Problems from validateDecision, shown inline. */
  problems: string[];
  hasContinue: boolean;
  /** Stream F (Section 22 shell). A person's row highlights across panels when hovered. */
  hoveredPersonId: PersonId | null;
  /** The open prompt the event overlay shows. */
  promptId: PromptId | null;
  listFilter: ListFilter;
  peopleView: PeopleView;
  autoFill: AutoFillProposal | null;
  /** The End season confirm summary is open. */
  confirmingEnd: boolean;
  notice: Notice | null;
  observerRunning: boolean;
  olderBalance: OlderBalance | null;
  /** Which part of the saves overlay has focus. */
  savesMode: 'save' | 'load';
}

export const initialUi: UiState = {
  tab: 'people',
  overlay: null,
  selectedPersonId: null,
  error: null,
  problems: [],
  hasContinue: false,
  hoveredPersonId: null,
  promptId: null,
  listFilter: { kind: 'all' },
  peopleView: initialPeopleView,
  autoFill: null,
  confirmingEnd: false,
  notice: null,
  observerRunning: false,
  olderBalance: null,
  savesMode: 'load',
};

export const game = signal<GameState | null>(null);
export const draft = signal<Decision | null>(null);
export const status = signal<SeasonStatus>('title');
export const ui = signal<UiState>(initialUi);

/** Section 22 Settings, persisted under `utopia:settings` (stream F). */
export const settings = signal<Settings>(DEFAULT_SETTINGS);

/** Section 24.14 "history" signal: metadata of the autosave ring and the named slots. */
export const saves = signal<{ autosaves: SaveMeta[]; slots: SaveMeta[] }>({
  autosaves: [],
  slots: [],
});

/** Patch UI state immutably. */
export function patchUi(patch: Partial<UiState>): void {
  ui.value = { ...ui.value, ...patch };
}
